import { useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { cx, rovingIndex } from './util.ts';
import './Tabs.css';

export interface TabItem {
  id: string;
  label: ReactNode;
  content: ReactNode;
  /** Contador o insignia junto a la etiqueta («3»). */
  badge?: ReactNode;
}

export interface TabsProps {
  /** Nombre accesible de la lista de pestañas. */
  label: string;
  items: readonly TabItem[];
  /** Controlado: pestaña activa. */
  value?: string;
  defaultValue?: string;
  onChange?: (id: string) => void;
  className?: string;
}

/** Pestañas con activación automática: las flechas, Inicio y Fin cambian de pestaña. */
export function Tabs({ label, items, value, defaultValue, onChange, className }: TabsProps) {
  const base = useId();
  const [inner, setInner] = useState(defaultValue ?? items[0]?.id);
  const active = value ?? inner;
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const activeIndex = Math.max(
    0,
    items.findIndex((t) => t.id === active),
  );

  const select = (id: string) => {
    setInner(id);
    onChange?.(id);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const next = rovingIndex(event.key, index, items.length);
    if (next === null || event.key === 'ArrowUp' || event.key === 'ArrowDown') return;
    event.preventDefault();
    select(items[next]!.id);
    refs.current[next]?.focus();
  };

  return (
    <div className={cx('af-tabs', className)}>
      <div role="tablist" aria-label={label} className="af-tabs__list">
        {items.map((item, i) => {
          const selected = i === activeIndex;
          return (
            <button
              key={item.id}
              ref={(el) => {
                refs.current[i] = el;
              }}
              type="button"
              role="tab"
              id={`${base}-tab-${item.id}`}
              aria-selected={selected}
              aria-controls={`${base}-panel-${item.id}`}
              tabIndex={selected ? 0 : -1}
              className="af-tabs__tab"
              onClick={() => select(item.id)}
              onKeyDown={(e) => onKeyDown(e, i)}
            >
              <span>{item.label}</span>
              {item.badge !== undefined && <span className="af-tabs__badge">{item.badge}</span>}
            </button>
          );
        })}
      </div>
      {items.map((item, i) => (
        <div
          key={item.id}
          role="tabpanel"
          id={`${base}-panel-${item.id}`}
          aria-labelledby={`${base}-tab-${item.id}`}
          tabIndex={0}
          hidden={i !== activeIndex}
          className="af-tabs__panel"
        >
          {item.content}
        </div>
      ))}
    </div>
  );
}
